import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FlaskConical, KeyRound, Send, Sparkles, XCircle } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Card } from '../../../shared/components/ui/Card.js';
import { Input } from '../../../shared/components/ui/Input.js';
import { aiApi, type PropagateResult, type ResultadoDoTeste } from '../api.js';

/**
 * Configuração da melhoria por IA.
 *
 * Esta tela é a fonte da verdade; os servidores ChatPlus guardam uma cópia pra
 * não pararem junto com o painel. Editar aqui NÃO chega lá sozinho de imediato —
 * por isso o botão "Enviar aos servidores" existe e a tela avisa quando há
 * edição mais nova que a última propagação. (Mesmo esquecendo, cada servidor
 * pergunta sozinho a cada poucos minutos e se atualiza.)
 */
export function AiSettingsPage() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['ai-settings'], queryFn: () => aiApi.get() });

  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState<'gemini' | 'openai'>('gemini');
  const [model, setModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [prompt, setPrompt] = useState('');
  const [timeoutMs, setTimeoutMs] = useState(4000);
  const [resultado, setResultado] = useState<PropagateResult | null>(null);
  const [teste, setTeste] = useState<ResultadoDoTeste | null>(null);

  // Preenche o formulário quando os dados chegam. A chave fica em branco de
  // propósito: ela nunca volta do servidor, e branco significa "não mexi nela".
  useEffect(() => {
    const d = query.data;
    if (!d) return;
    setEnabled(d.enabled);
    setProvider(d.provider);
    setModel(d.model);
    setPrompt(d.prompt);
    setTimeoutMs(d.timeout_ms);
  }, [query.data]);

  /**
   * Catálogo do fornecedor. Recarrega quando o fornecedor muda (está na chave),
   * então trocar Gemini↔OpenAI já mostra a lista certa antes de salvar.
   *
   * Usa a chave GUARDADA — uma chave nova ainda não salva no campo acima não
   * vale aqui. Salvar primeiro, e a lista vem.
   */
  // Lista fixa no servidor: não depende de chave nem do fornecedor estar de pé,
  // e troca junto com o fornecedor selecionado.
  const modelosQuery = useQuery({
    queryKey: ['ai-models', provider],
    queryFn: () => aiApi.models(provider),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const modelos = modelosQuery.data?.models ?? [];

  // Modelo vazio (troca de fornecedor, ou config nunca preenchida) assume o
  // primeiro da lista — que é o mais novo, já que a ordem é decrescente.
  useEffect(() => {
    if (!model && modelos.length > 0) setModel(modelos[0] as string);
  }, [model, modelos]);
  const motivoDaLista = modelosQuery.isError
    ? 'Não foi possível carregar a lista; digite o nome do modelo à mão.'
    : null;

  /**
   * Experimenta a configuração ATUAL do formulário (sem depender de salvar).
   *
   * Sem isto, dava pra trocar o fornecedor e manter chave e modelo do outro:
   * a tela dizia "Configuração salva" e o estrago só apareceria em produção,
   * como reescrita que nunca acontece.
   */
  const experimentar = useMutation({
    mutationFn: async () =>
      aiApi.test({
        provider,
        model,
        prompt,
        ...(apiKey.length > 0 && { api_key: apiKey }),
      }),
    onSuccess: (r) => {
      setTeste(r);
      if (r.ok) toast.success(`A IA passou nos ${r.casos?.length ?? 0} casos em ${r.ms} ms`);
      else toast.error('A configuração não funcionou', { description: r.erro });
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  const salvar = useMutation({
    mutationFn: async () =>
      aiApi.save({
        enabled,
        provider,
        model,
        prompt,
        timeout_ms: timeoutMs,
        ...(apiKey.length > 0 && { api_key: apiKey }),
      }),
    onSuccess: () => {
      setApiKey('');
      toast.success('Configuração salva', {
        description: 'Testando com a IA antes de você enviar…',
      });
      void qc.invalidateQueries({ queryKey: ['ai-settings'] });
      void qc.invalidateQueries({ queryKey: ['ai-models'] });
      // Salvar sozinho não prova nada — o teste é que diz se a configuração
      // funciona. Roda automaticamente pra você não precisar lembrar.
      experimentar.mutate();
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  const propagar = useMutation({
    mutationFn: async () => aiApi.propagate(),
    onSuccess: (r) => {
      setResultado(r);
      const ok = r.servers.filter((s) => s.ok).length;
      if (ok === r.servers.length) {
        toast.success(`Enviado a ${ok} servidor(es)`);
      } else {
        // Falha aqui não é urgência: quem não recebeu se corrige no pull.
        toast.warning(`${ok} de ${r.servers.length} servidores atualizados`, {
          description: 'Quem falhou se atualiza sozinho em alguns minutos.',
        });
      }
      void qc.invalidateQueries({ queryKey: ['ai-settings'] });
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    salvar.mutate();
  };

  const d = query.data;
  // Cada fornecedor guarda a sua chave, então o campo fala do que está escolhido.
  const temChaveDoFornecedor =
    provider === 'openai' ? Boolean(d?.has_key_openai) : Boolean(d?.has_key_gemini);
  const nomeDoFornecedor = provider === 'openai' ? 'OpenAI' : 'Google Gemini';
  // Editou depois da última vez que mandou? Então os servidores estão com a
  // versão antiga até o próximo pull.
  const pendente =
    d?.updated_at && (!d.propagated_at || new Date(d.updated_at) > new Date(d.propagated_at));

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center gap-2.5">
        <Sparkles size={20} className="text-brand-500" />
        <div className="flex flex-col leading-tight">
          <h1 className="font-bold text-[18px] text-n-900">Melhoria por IA</h1>
          <p className="text-[12px] text-n-500">
            Reescreve as mensagens agrupadas antes de saírem pro cliente. A conta é da
            plataforma — quem tem direito é definido por empresa.
          </p>
        </div>
      </div>

      {query.isLoading && <p className="text-[13px] text-n-500">Carregando…</p>}

      {d && (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Card className="flex flex-col gap-4 p-5">
            <label className="flex items-center gap-2.5">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="h-4 w-4 accent-brand-500"
              />
              <span className="font-semibold text-[13px] text-n-800">
                Ligada para a plataforma
              </span>
              <span className="text-[12px] text-n-500">
                Desligado aqui, nenhum canal reescreve — mesmo os que ligaram a opção.
              </span>
            </label>

            <div className="grid grid-cols-3 gap-4">
              <div className="flex flex-col gap-1.5">
                <span className="font-medium text-[12px] text-n-700">Fornecedor</span>
                <select
                  value={provider}
                  onChange={(e) => {
                    setProvider(e.target.value as 'gemini' | 'openai');
                    // Sem isto, o modelo do fornecedor anterior continuava
                    // escolhido e aparecia como "fora do catálogo" na lista do
                    // novo — `gpt-5-nano` oferecido dentro do Gemini. O efeito
                    // abaixo escolhe o primeiro da lista nova assim que ela chega.
                    setModel('');
                    setTeste(null);
                    // O que estiver digitado é a chave do fornecedor anterior.
                    setApiKey('');
                  }}
                  className="h-[36px] rounded-md border border-n-200 bg-white px-3 text-[14px] text-n-900 focus:border-brand-500 focus:outline-none"
                >
                  <option value="gemini">Google Gemini</option>
                  <option value="openai">OpenAI</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="font-medium text-[12px] text-n-700">Modelo</span>
                {modelos.length > 0 ? (
                  <select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className="h-[36px] rounded-md border border-n-200 bg-white px-3 text-[14px] text-n-900 focus:border-brand-500 focus:outline-none"
                  >
                    {/* O que está salvo pode não estar mais no catálogo (modelo
                        aposentado, ou nome digitado antes desta tela existir).
                        Sem esta opção, abrir a tela trocaria o modelo sozinho. */}
                    {model && !modelos.includes(model) && (
                      <option value={model}>{model} (fora do catálogo)</option>
                    )}
                    {modelos.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    placeholder={provider === 'gemini' ? 'gemini-3.1-flash-lite' : 'gpt-4o-mini'}
                    className="h-[36px] rounded-md border border-n-200 bg-white px-3 text-[14px] text-n-900 placeholder:text-n-400 focus:border-brand-500 focus:outline-none"
                  />
                )}
                <span className="text-[11px] text-n-500">
                  {modelosQuery.isLoading
                    ? 'Carregando…'
                    : (motivoDaLista ??
                      `${modelos.length} modelos. Lançou um novo? Peça pra incluir na lista.`)}
                </span>
              </div>

              <Input
                label="Tempo máximo de espera (ms)"
                type="number"
                min={500}
                max={30000}
                value={timeoutMs}
                onChange={(e) => setTimeoutMs(Number(e.target.value))}
                hint="Passou disso, a mensagem sai como foi digitada."
              />
            </div>

            <Input
              label={`Chave da API — ${nomeDoFornecedor}`}
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={
                temChaveDoFornecedor
                  ? '•••••••• (guardada — digite pra trocar)'
                  : `cole a chave da ${nomeDoFornecedor}`
              }
              hint={
                temChaveDoFornecedor
                  ? 'Deixe em branco pra manter a chave atual. Cada fornecedor guarda a sua — trocar de fornecedor não apaga a outra.'
                  : `Ainda não há chave da ${nomeDoFornecedor} guardada — sem ela nada é reescrito.`
              }
            />

            <div className="flex flex-col gap-1.5">
              <span className="font-medium text-[12px] text-n-700">
                Instruções para a IA
              </span>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={16}
                className="rounded-md border border-n-200 bg-white p-3 font-mono text-[12px] text-n-900 leading-relaxed focus:border-brand-500 focus:outline-none"
              />
              <span className="text-[11px] text-n-500">
                É aqui que se ajusta o resultado. As duas linhas que mais trabalham são a
                de copiar números exatamente (preço, dosagem, chave PIX) e a de responder
                só com a mensagem final.
              </span>
            </div>
          </Card>

          <div className="flex items-center gap-3">
            <Button type="submit" loading={salvar.isPending}>
              <KeyRound size={14} />
              Salvar
            </Button>
            <Button
              type="button"
              variant="outline"
              loading={experimentar.isPending}
              onClick={() => experimentar.mutate()}
            >
              <FlaskConical size={14} />
              Testar
            </Button>
            <Button
              type="button"
              variant="outline"
              loading={propagar.isPending}
              onClick={() => {
                // Propagar uma configuração que não funciona espalha o defeito
                // pra todos os servidores — e lá ele é invisível, porque falhar
                // significa mandar o texto original. Confirma antes.
                if (teste && !teste.ok) {
                  const segue = window.confirm(
                    `O último teste falhou:\n\n${teste.erro}\n\n` +
                      'Enviar assim deixa todos os servidores sem reescrever nenhuma ' +
                      'mensagem, e isso não aparece como erro pra ninguém.\n\nEnviar mesmo assim?',
                  );
                  if (!segue) return;
                }
                propagar.mutate();
              }}
            >
              <Send size={14} />
              Enviar aos servidores
            </Button>
            {pendente && (
              <span className="text-[12px] text-warning-700">
                Há alterações que ainda não foram enviadas.
              </span>
            )}
            {d.propagated_at && (
              <span className="text-[12px] text-n-500">
                Último envio: {new Date(d.propagated_at).toLocaleString('pt-BR')}
              </span>
            )}
          </div>
        </form>
      )}

      {teste && (
        <Card className="flex flex-col gap-3 p-5">
          <div className="flex items-center gap-2">
            {teste.ok ? (
              <CheckCircle2 size={16} className="text-success-600" />
            ) : (
              <XCircle size={16} className="text-danger-500" />
            )}
            <span className="font-semibold text-[13px] text-n-800">
              {teste.ok
                ? `A IA respondeu aos ${teste.casos?.length ?? 0} casos`
                : 'A configuração não funcionou'}
            </span>
            {teste.ms !== undefined && (
              <span className="text-[12px] text-n-500">
                {teste.ms} ms no total
                {teste.tokens_in !== undefined &&
                  ` · ${teste.tokens_in} tokens de entrada, ${teste.tokens_out} de saída`}
              </span>
            )}
          </div>

          {teste.erro && (
            <p className="rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
              {teste.erro}
            </p>
          )}

          {teste.casos?.map((c) => (
            <div key={c.nome} className="flex flex-col gap-1.5 border-n-100 border-t pt-3">
              <div className="flex items-center gap-2">
                <span className="font-medium text-[12px] text-n-700">{c.nome}</span>
                <span className="text-[11px] text-n-400">{c.ms} ms</span>
                {c.numeros_perdidos && c.numeros_perdidos.length > 0 && (
                  // O erro caro: um número do original que mudou ou sumiu.
                  <span className="rounded-pill bg-danger-50 px-2 py-0.5 font-bold text-[10px] text-danger-700">
                    número alterado: {c.numeros_perdidos.join(', ')}
                  </span>
                )}
              </div>
              {c.ok ? (
                <div className="grid grid-cols-2 gap-3">
                  <pre className="whitespace-pre-wrap rounded-md bg-n-50 p-2.5 font-mono text-[11.5px] text-n-700">
                    {c.entrada}
                  </pre>
                  <pre
                    className={`whitespace-pre-wrap rounded-md p-2.5 font-mono text-[11.5px] text-n-800 ${
                      c.numeros_perdidos && c.numeros_perdidos.length > 0
                        ? 'bg-danger-50'
                        : 'bg-success-50'
                    }`}
                  >
                    {c.texto}
                  </pre>
                </div>
              ) : (
                <p className="rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
                  {c.erro}
                </p>
              )}
            </div>
          ))}
        </Card>
      )}

      {resultado && (
        <Card className="flex flex-col gap-2 p-5">
          <span className="font-semibold text-[13px] text-n-800">Resultado do envio</span>
          {resultado.servers.length === 0 && (
            <span className="text-[12px] text-n-500">Nenhum servidor ativo cadastrado.</span>
          )}
          {resultado.servers.map((s) => (
            <div key={s.server_id} className="flex items-center gap-2 text-[12px]">
              {s.ok ? (
                <CheckCircle2 size={14} className="text-success-600" />
              ) : (
                <XCircle size={14} className="text-danger-500" />
              )}
              <span className="font-medium text-n-800">{s.server_name}</span>
              <span className="text-n-500">{s.detail}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
